/**
 * Utilitário para impressão em impressoras térmicas Bluetooth via Web.
 * Gera um HTML compacto otimizado para papel térmico (80mm ~ 576px / 58mm ~ 300px).
 */

export interface ThermalPrintData {
  title?: string
  subtitle?: string
  lines: Array<{ label?: string; text: string; bold?: boolean; align?: 'left' | 'center' | 'right' }>
  separator?: boolean
}

function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

export function printThermal(title: string, content: ThermalPrintData[], options?: { width?: 58 | 80 }) {
  const width = options?.width ?? 80

  const htmlLines = content
    .map(section => {
      const sectionTitle = section.title ? `<div class="sec-title">${escapeHtml(section.title)}</div>` : ''
      const sectionSub = section.subtitle ? `<div class="sec-sub">${escapeHtml(section.subtitle)}</div>` : ''
      const lines = section.lines
        .map(line => {
          const align = line.align ?? 'left'
          const cls = line.bold ? 'bold' : ''
          const label = line.label ? `<span class="lbl">${escapeHtml(line.label)}</span> ` : ''
          return `<div class="row ${align} ${cls}">${label}${escapeHtml(line.text)}</div>`
        })
        .join('')
      const sep = section.separator ? `<div class="sep">────────────────────</div>` : ''
      return `${sectionTitle}${sectionSub}${lines}${sep}`
    })
    .join('')

  const mmWidth = width === 58 ? 58 : 80

  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <title>Imprimir</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body {
      width: ${mmWidth}mm;
      max-width: ${mmWidth}mm;
      font-family: 'Courier New', Courier, monospace;
      font-size: 13px;
      font-weight: bold;
      line-height: 1.3;
      color: #000;
      padding: 3px;
    }
    @media print {
      @page { size: ${mmWidth}mm auto; margin: 0; }
      * { margin:0; padding:0; }
      body { width: ${mmWidth}mm; max-width: ${mmWidth}mm; padding: 2px; }
    }
    .center { text-align:center; }
    .right  { text-align:right; }
    .left   { text-align:left; }
    .bold   { font-weight: bold; }
    .row    { padding: 1px 0; word-break: break-word; margin:0; }
    .lbl    { font-weight: bold; }
    .title  { font-weight: bold; font-size: 15px; text-align:center; margin-bottom: 3px; margin-top:0; }
    .sep    { text-align:center; margin: 3px 0; font-size: 12px; line-height:1; }
    .sec-title { font-weight:bold; margin-top:3px; font-size: 13px; margin-bottom:0; }
    .sec-sub   { font-size:11px; margin-bottom:0; }
    table { width:100%; border-collapse:collapse; margin:0; }
    th, td { padding: 1px 0; text-align:left; font-size: 12px; line-height:1.3; }
    th { font-weight:bold; }
    td.right, th.right { text-align:right; }
    p { margin:0; padding:0; }
  </style>
</head>
<body>
  ${htmlLines}
</body>
</html>`

  // Usa iframe oculto para acionar window.print() — funciona em mobile/Bluetooth
  const existingFrame = document.getElementById('__thermal_print_frame')
  if (existingFrame) existingFrame.remove()

  const frame = document.createElement('iframe')
  frame.id = '__thermal_print_frame'
  frame.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;border:none;visibility:hidden;'
  document.body.appendChild(frame)

  const frameDoc = frame.contentDocument || frame.contentWindow?.document
  if (frameDoc) {
    frameDoc.open()
    frameDoc.write(html)
    frameDoc.close()
  }

  setTimeout(() => {
    try {
      frame.contentWindow?.focus()
      frame.contentWindow?.print()
    } finally {
      setTimeout(() => frame.remove(), 2000)
    }
  }, 400)
}

export function printComprovanteInscricaoThermal(opts: {
  atirador_nome: string
  atirador_cpf: string
  clube_nome: string
  campeonato_descricao: string
  etapa_titulo: string
  modalidade_nome: string
  numero_inscricao: number
  categoria: string
  valor_cobrado: number | string
  forma_pagamento?: string | null
  status: string
  data?: string
}) {
  const f = (n: number | string | null) => {
    if (n === null || n === undefined) return 'R$ --'
    const val = typeof n === 'string' ? parseFloat(n) : n
    return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  }

  const formaMap: Record<string, string> = { pix: 'PIX', cartao: 'Cartao', pos: 'POS', isento: 'Isento' }
  const forma = opts.forma_pagamento ? (formaMap[opts.forma_pagamento] ?? opts.forma_pagamento) : '--'

  printThermal('Comprovante', [
    {
      title: 'CAMPEONATO DE TIRO',
      lines: [
        { text: 'COMPROVANTE DE INSCRICAO', bold: true, align: 'center' },
      ],
      separator: true,
    },
    {
      lines: [
        { label: 'Atirador:', text: opts.atirador_nome },
        { label: 'CPF:', text: opts.atirador_cpf },
        { label: 'Clube:', text: opts.clube_nome },
      ],
      separator: true,
    },
    {
      lines: [
        { label: 'Campeonato:', text: opts.campeonato_descricao },
        { label: 'Etapa:', text: opts.etapa_titulo },
        { label: 'Modalidade:', text: opts.modalidade_nome },
        { label: 'Categoria:', text: opts.categoria },
        { label: 'Insc:', text: `#${opts.numero_inscricao}` },
      ],
      separator: true,
    },
    {
      lines: [
        { label: 'Pagamento:', text: forma },
        { label: 'Status:', text: opts.status },
        { label: 'Valor:', text: f(opts.valor_cobrado), bold: true },
      ],
      separator: true,
    },
    {
      lines: [
        { text: opts.data ?? new Date().toLocaleString('pt-BR'), align: 'center' },
      ],
    },
  ])
}

export function printComprovanteResultadoThermal(opts: {
  atirador_nome: string
  cpf?: string
  campeonato_descricao?: string
  etapa_titulo?: string
  modalidade_nome: string
  total_pontos: number | string
  qtd_x?: number | string
  qtd_10?: number | string
  posicao?: number | string
  data?: string
}) {
  printThermal('Resultado', [
    {
      title: 'CAMPEONATO DE TIRO',
      lines: [{ text: 'COMPROVANTE DE RESULTADO', bold: true, align: 'center' }],
      separator: true,
    },
    {
      lines: [
        { label: 'Atirador:', text: opts.atirador_nome },
        { label: 'CPF:', text: opts.cpf ?? '--' },
        { label: 'Campeonato:', text: opts.campeonato_descricao ?? '--' },
        { label: 'Etapa:', text: opts.etapa_titulo ?? '--' },
        { label: 'Modalidade:', text: opts.modalidade_nome },
      ],
      separator: true,
    },
    {
      lines: [
        { label: 'Total Pontos:', text: String(opts.total_pontos), bold: true },
        ...(opts.qtd_x !== undefined ? [{ label: 'Qtd X:', text: String(opts.qtd_x) }] : []),
        ...(opts.qtd_10 !== undefined ? [{ label: 'Qtd 10:', text: String(opts.qtd_10) }] : []),
        ...(opts.posicao !== undefined ? [{ label: 'Posicao:', text: String(opts.posicao) }] : []),
      ],
      separator: true,
    },
    {
      lines: [
        { text: opts.data ?? new Date().toLocaleString('pt-BR'), align: 'center' },
      ],
    },
  ])
}
